import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import {
  linkBank,
  linkBankIndividual,
  verifyBankAccount,
} from "actions/bankActions";
import { bankConstants, employerConstants } from "actions/types";
import classnames from "classnames";
import { get, isEmpty, isNil, noop } from "lodash";
import React from "react";
import { FiDollarSign } from "react-icons/fi";
import { connect, type ConnectedProps } from "react-redux";
import { toast } from "react-toastify";
import { createLoadingSelector } from "store/selectors";
import {
  bankSelector,
  isBankPending,
  isBankVerified,
  isPendingManualBankVerification,
} from "store/selectors/bank";
import { isEmployerSelector } from "store/selectors/user";
import type { RootState } from "types/state";
import type { UserBank } from "types/models";

import PlaidLink from "components/PlaidLink";
import Button from "components/Button";
import Accordian from "./Accordian";
import { hasAddedBank } from "store/selectors/employer";

interface PlaidInstance {
  handleOnClick?: () => void;
}

interface AddBankCardWithPlaidProps {
  plaidInstance?: PlaidInstance;
  bankAccount?: UserBank;
  isAddingBank?: boolean;
  isEmployer?: boolean;
}

const AddBankCardWithPlaid = ({
  // The runtime default is `{}`; cast to satisfy the selector signatures —
  // they tolerate an empty object via lodash `get`.
  plaidInstance = {},
  isAddingBank = false,
  isEmployer = false,
  bankAccount = {} as UserBank,
}: AddBankCardWithPlaidProps) => {
  const isPending = isBankPending(bankAccount);
  const isActive = isBankVerified(bankAccount);
  const isPendingManualVerification =
    isPendingManualBankVerification(bankAccount);

  const cardWrapperClass = classnames("add-bank-card", {
    "action-card": !isActive && !isAddingBank,
    "completed-card": isActive,
  });

  const cardTitle = () => {
    if (isPending) {
      return "Bank Account is Being Verified";
    }
    return isEmployer ? "Link Company Bank Account" : "Link Your Bank Account";
  };

  const cardDescription = () => {
    if (isPendingManualVerification) {
      return "Verify the two small deposits in your account after one to two business days.";
    } else if (isPending) {
      return "Your bank account will be automatically verified in one to two business days.";
    }
    return isEmployer
      ? "Link your company bank account to fund plan payroll contributions."
      : "Link your bank account to start making contributions.";
  };

  const cardAction = () => {
    if (isPendingManualVerification) {
      return "Verify";
    }
    return "Link Bank";
  };

  return (
    <div className={cardWrapperClass}>
      <Accordian
        onClick={plaidInstance.handleOnClick || noop}
        icon={<FiDollarSign color="white" stroke="#60A4BF" size={16} />}
        title={cardTitle()}
        description={cardDescription()}
        actionText={cardAction()}
        completedAction={
          (isActive || isPending) && !isPendingManualVerification
        }
        loading={isAddingBank}
        isFetching={isAddingBank}
      />
    </div>
  );
};

interface BankLinkDisclaimerProps {
  onContinue: () => void;
  isLoading?: boolean;
}

const BankLinkDisclaimer = ({
  onContinue,
  isLoading,
}: BankLinkDisclaimerProps) => {
  return (
    <div className="bank-link-disclaimer">
      <div className="bank-link-disclaimer__icon">
        <div className="icon">
          <FiDollarSign color="white" stroke="#60A4BF" size={16} />
        </div>
      </div>
      <div className="bank-link-disclaimer__content">
        <p className="bank-link-disclaimer__title">Link Company Bank Account</p>
        <p className="bank-link-disclaimer__text">
          Ensure that your bank will allow Icon&apos;s payroll debits by
          default. We have a support article on how to
          <a
            className="icon-link"
            href="https://support.iconsavingsplan.com/en/articles/11403709-how-to-whitelist-icon-in-your-bank-account"
            target="_blank"
            rel="noopener noreferrer"
          >
            configure this.
          </a>
          Denied debits can lead to extra fees and account verifications.
        </p>
        <Button
          size="sm"
          name="action"
          onClick={onContinue}
          loading={isLoading}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

const addingBankSelector = createLoadingSelector([
  bankConstants.LINK_BANK_WITH_ICON,
  employerConstants.UPDATE_PROGRESSIVE_ONBOARDING,
]);

const getBankAccount = (
  state: RootState,
  props: OwnProps
): UserBank | undefined => {
  /*
    This is necessary for multi-bank support for employers. Since we are displaying multiple add bank cards now,
    we need to pass in bank account from a list as a prop so we can render the correct state. This also maintains
    backwards compatibility for individuals by fetching whatever bank account exists in redux if none is passed as a prop
  */
  if (props.insertOnlyMode) {
    // Runtime returns the empty-object sentinel; cast to satisfy the return
    // type while preserving the exact `{}` value the component tolerates.
    return {} as UserBank;
  }
  if (isEmpty(props.bankAccount)) {
    // Preserved quirk: bankSelector falls back to undefined when
    // accounts.bankAccounts is empty; the component tolerates undefined.
    return bankSelector(state);
  }
  return props.bankAccount;
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const bankAccount = getBankAccount(state, ownProps);
  return {
    isEmployer: isEmployerSelector(state),
    isAddingBank: addingBankSelector(state),
    companyId: get(state.employer, "company.id", null),
    bankAccount,
    disclaimerAcknowledged: hasAddedBank(state),
  };
};

const mapDispatchToProps = {
  linkBankIndividual,
  linkBank,
  verifyBankAccount,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// companyId is supplied by mapStateToProps (PropsFromRedux), not by callers,
// so it is intentionally not declared here.
interface OwnProps {
  bankAccount?: UserBank;
  onSuccess?: (data: unknown) => void;
  insertOnlyMode?: boolean;
  isEmployerDashboard?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  showDisclaimer: boolean;
  linkedBankData: unknown;
}

class AddEmployerBankCard extends React.PureComponent<Props, State> {
  static defaultProps = {
    insertOnlyMode: false,
    onSuccess: noop,
  };

  state: State = {
    showDisclaimer: false,
    linkedBankData: null,
  };

  _handleOnSuccess = (token: string, metadata: unknown, ownerName: string) => {
    if (isPendingManualBankVerification(this.props.bankAccount)) {
      void this.props.verifyBankAccount(
        this.props.client as ApolloClient<object>,
        this.props.bankAccount!.id
      );
      return;
    }
    if (this.props.isEmployer) {
      void this.props
        .linkBank(
          this.props.client as ApolloClient<object>,
          token,
          metadata as Parameters<typeof linkBank>[2]
        )
        .then((data) => {
          // @ts-expect-error data is the dispatched action union; the inferred
          // type narrows to the SUCCESS action, but the FAILURE action carries
          // `error` at runtime — preserved guard from the original JS.
          if (data && isNil(data.error)) {
            toast.success("Successfully linked bank account.");
            this.setState({ showDisclaimer: true, linkedBankData: data });
          }
        });
    } else {
      void this.props
        .linkBankIndividual(
          this.props.client as ApolloClient<object>,
          token,
          metadata as Parameters<typeof linkBankIndividual>[2],
          ownerName
        )
        .then((data) => {
          // @ts-expect-error data is the dispatched action union; the inferred
          // type narrows to the SUCCESS action, but the FAILURE action carries
          // `error` at runtime — preserved guard from the original JS.
          if (data && isNil(data.error)) {
            toast.success("Successfully linked bank account.");
            this.props.onSuccess!(data);
          }
        });
    }
  };

  _handleDisclaimerContinue = () => {
    const { linkedBankData } = this.state;
    this.setState({ showDisclaimer: false, linkedBankData: null });
    this.props.onSuccess!(linkedBankData || this.props.bankAccount);
  };

  _noLinkedBankRender = (plaidInstance: React.Component) => {
    return (
      <AddBankCardWithPlaid
        // PlaidLink forwards its own instance here; it exposes handleOnClick,
        // which AddBankCardWithPlaid reads. Narrow to the shape it uses.
        plaidInstance={plaidInstance as unknown as PlaidInstance}
        bankAccount={this.props.bankAccount}
        isAddingBank={this.props.isAddingBank}
        isEmployer={this.props.isEmployer}
      />
    );
  };

  _shouldShowDisclaimer = () => {
    const { showDisclaimer } = this.state;
    const { isEmployer, bankAccount, disclaimerAcknowledged } = this.props;

    if (disclaimerAcknowledged) {
      return false;
    }

    if (showDisclaimer) {
      return true;
    }

    if (
      isEmployer &&
      !isEmpty(bankAccount) &&
      !isPendingManualBankVerification(bankAccount)
    ) {
      return true;
    }

    return false;
  };

  render() {
    if (this._shouldShowDisclaimer()) {
      return (
        <BankLinkDisclaimer
          onContinue={this._handleDisclaimerContinue}
          isLoading={this.props.isAddingBank}
        />
      );
    }

    if (
      isEmpty(this.props.bankAccount) ||
      isPendingManualBankVerification(this.props.bankAccount)
    ) {
      return (
        <PlaidLink
          // @ts-expect-error isAddingBank is not a PlaidLink prop; it was
          // passed (and ignored) in the original JS — preserved as an inert
          // pass-through.
          isAddingBank={this.props.isAddingBank}
          onSuccess={this._handleOnSuccess}
          render={this._noLinkedBankRender}
          bank={this.props.bankAccount}
        />
      );
    }

    return (
      <AddBankCardWithPlaid
        bankAccount={this.props.bankAccount}
        isAddingBank={this.props.isAddingBank}
        isEmployer={this.props.isEmployer}
      />
    );
  }
}

export default connector(withApollo<Props>(AddEmployerBankCard));
